/**
 * Casos de uso de pago (ENA-1607): armado de requests de `pre-initiate` / `initiate` y
 * normalización del resultado para el host.
 */
import { PaymentRequestError } from '../domain/errors';
import type {
  InitiateRequest,
  InitiateResponse,
  PaymentOutcome,
  PreInitiateRequest,
  PreInitiateResponse,
  SavedCard,
} from '../domain/models';
import type { PaymentMiddlewareClient } from '../infrastructure/paymentMiddlewareClient';
import { translate } from '../i18n';
import type { ChargeWithPaymentToken } from './paymentTokens';
import { normalizePaymentAmounts } from '../utils/moneyAmount';

const PROVIDER = 'PAYWAY' as const;

export interface CardCharge {
  card: SavedCard;
  amount: number;
}

export interface CardChargeWithInstallments extends CardCharge {
  installments: number;
}

const SESSION_BIN_PREFIX_LENGTH = 9;

/** `paymentMethodId`: solo lo traen las tarjetas guardadas (GET); las nuevas viajan sin él. */
const savedCardMethod = (card: SavedCard): { paymentMethodId?: number } =>
  card.paymentMethodId !== undefined ? { paymentMethodId: card.paymentMethodId } : {};

/**
 * BIN del instrumento en `pre-initiate` / `initiate`.
 * - Guardada (GET): 6 dígitos + `paymentMethodId`.
 * - Nueva tokenizada: 9 primeros dígitos del PAN (`extendedBin`), p. ej. `450799000` para Visa test.
 */
export const resolveInstrumentBin = (card: SavedCard): string => {
  if (card.paymentMethodId !== undefined) {
    return card.bin.replace(/\D/g, '').slice(0, 6);
  }
  const extended = card.extendedBin?.replace(/\D/g, '');
  if (extended && extended.length >= SESSION_BIN_PREFIX_LENGTH) {
    return extended.slice(0, SESSION_BIN_PREFIX_LENGTH);
  }
  const binDigits = card.bin.replace(/\D/g, '');
  if (binDigits.length >= SESSION_BIN_PREFIX_LENGTH) {
    return binDigits.slice(0, SESSION_BIN_PREFIX_LENGTH);
  }
  if (binDigits.length === 6) {
    return `${binDigits}000`;
  }
  return card.bin;
};

export const buildPreInitiateRequest = (charges: CardChargeWithInstallments[]): PreInitiateRequest => {
  const { amounts } = normalizePaymentAmounts(charges.map(({ amount }) => amount));
  return {
    instruments: charges.map(({ card, installments }, index) => ({
      provider: PROVIDER,
      bin: resolveInstrumentBin(card),
      amount: amounts[index],
      installments,
      ...savedCardMethod(card),
    })),
  };
};

export const buildPreInitiateRequestFromAmounts = (charges: CardCharge[]): PreInitiateRequest =>
  buildPreInitiateRequest(charges.map((charge) => ({ ...charge, installments: 1 })));

export const buildInitiateRequest = (charges: ChargeWithPaymentToken[]): InitiateRequest => {
  const { amounts, totalAmount } = normalizePaymentAmounts(charges.map(({ amount }) => amount));
  return {
    provider: PROVIDER,
    totalAmount,
    instruments: charges.map(({ card, installments, paymentToken }, index) => ({
      token: paymentToken,
      // Mismo patrón 6|9 dígitos que `pre-initiate` (string). TODO(ENA-1607): validar con un pago aprobado.
      bin: resolveInstrumentBin(card),
      lastDigits: card.lastFourDigits,
      installments,
      amount: amounts[index],
      ...savedCardMethod(card),
    })),
  };
};

export const loadInstallments = (
  client: PaymentMiddlewareClient,
  charges: CardChargeWithInstallments[],
): Promise<PreInitiateResponse> => client.preInitiate(buildPreInitiateRequest(charges));

const APPROVED = new Set(['APPROVED', 'COMPLETED']);
const PENDING = new Set(['PENDING', 'IN_PROCESS', 'PROCESSING']);
const REJECTED = new Set(['REJECTED', 'DECLINED', 'DENIED']);

export const toPaymentOutcome = (response: InitiateResponse): PaymentOutcome => {
  const paymentStatus = response.payment?.status?.toUpperCase() ?? '';
  const base = { paymentId: response.payment?.id, orderId: response.order?.orderId };

  // Con una acción pendiente (p. ej. 3DS) el pago no está resuelto: lo confirma el host.
  const hasPendingAction = (response.payment?.nextAction?.type ?? 'NONE') !== 'NONE';
  if (PENDING.has(paymentStatus) || hasPendingAction) {
    return { status: 'pending', ...base };
  }
  if (APPROVED.has(paymentStatus)) {
    return { status: 'approved', ...base };
  }
  if (REJECTED.has(paymentStatus)) {
    return { status: 'rejected', ...base, message: translate('paymentsUI.errorBox.paymentRejected') };
  }
  return { status: 'error', ...base, message: translate('paymentsUI.errorBox.processingError') };
};

export const processPayment = async (
  client: PaymentMiddlewareClient,
  charges: ChargeWithPaymentToken[],
): Promise<PaymentOutcome> => {
  try {
    return toPaymentOutcome(await client.initiate(buildInitiateRequest(charges)));
  } catch (error) {
    if (error instanceof PaymentRequestError && error.outcomeUnknown) {
      // Resultado incierto: el host verifica el pedido; no se reintenta (evita cobro duplicado).
      return { status: 'pending', message: translate('paymentsUI.errorBox.outcomeUnknown') };
    }
    // payment-service respondió "not-approved": el pago fue rechazado (no es un error técnico).
    if (error instanceof PaymentRequestError && /not-approved/i.test(error.detail ?? '')) {
      return { status: 'rejected', message: translate('paymentsUI.errorBox.paymentRejected') };
    }
    const detail = error instanceof PaymentRequestError ? error.detail ?? '' : '';
    if (
      /start-transaction-service\.bad-request/i.test(detail) ||
      /START-TRANSACTION-SERVICE-BAD_REQUEST/i.test(detail)
    ) {
      return {
        status: 'rejected',
        message: translate('paymentsUI.paymentFailure.paymentDetail'),
      };
    }
    return { status: 'error', message: translate('paymentsUI.errorBox.processingError') };
  }
};
